import CustomDialog from 'common/components/CustomDialog'
import useYupValidationResolver from 'common/hooks/useYupValidationResolver'
import { useMutateCreateCategory, useMutateEditCategory } from 'modules/inventory/api/mutation'
import { Category, CreateCategoryBody } from 'modules/inventory/api/type'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { Box, TextField } from '@mui/material'

interface CreateCategoryDialogProps {
  open: boolean
  onClose: () => void
  category?: Category
}

export default function CreateCategoryDialog({ open, onClose, category }: CreateCategoryDialogProps) {
  const { t } = useTranslation('category')
  const isEdit = Boolean(category)

  const schema = Yup.object().shape({
    name: Yup.string().required(t('please.input.category.name')),
  })
  const resolver = useYupValidationResolver(schema)

  const { control, handleSubmit, reset, formState: { errors } } = useForm<CreateCategoryBody>({
    defaultValues: { name: '' },
    resolver,
  })

  const createCategoryMutation = useMutateCreateCategory()
  const editCategoryMutation = useMutateEditCategory()

  useEffect(() => {
    if (open && isEdit && category) {
      reset({ name: category.name })
    } else if (open && !isEdit) {
      reset({ name: '' })
    }
  }, [open, isEdit, category, reset])

  const handleClose = () => {
    reset()
    onClose()
  }

  const onSubmit = async (data: CreateCategoryBody) => {
    if (isEdit && category) {
      await editCategoryMutation.mutateAsync({ body: { name: data.name }, categoryId: category.id })
    } else {
      await createCategoryMutation.mutateAsync({ name: data.name })
    }
    handleClose()
  }

  return (
    <CustomDialog
      open={open}
      closeDialog={handleClose}
      title={isEdit ? t('edit') : t('category.create')}
      submitLabel={t('category.save')}
      formName="category-dialog-form"
      isSubmitLoading={createCategoryMutation.isLoading || editCategoryMutation.isLoading}
      onSubmit={() => {}}
      enableCloseButton
      enableInLineWithTitle
      maxWidth="xs"
      contentMinHeight={100}
      fullWidth
      contentMinWidth={0}
    >
      <form id="category-dialog-form" onSubmit={handleSubmit(onSubmit)}>
        <Box>
          <label style={{ fontWeight: 600 }}>
            <span style={{ color: 'red', marginRight: 4 }}>*</span>
            {t('category.name')}
          </label>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                sx={{ mt: 0.5 }}
                placeholder={t('category.input')}
                error={!!errors.name}
                helperText={errors.name?.message}
                disabled={category?.is_default}
              />
            )}
          />
        </Box>
      </form>
    </CustomDialog>
  )
}
